/**
 * home.js — teksten van de homepage (/ en /en).
 * Vorm van NL en EN moet gelijk zijn (paginas.test.js).
 * Stukken met nadruk of een link staan als { voor, nadruk, na } zodat de JSX
 * in HomeHero.js dezelfde markup houdt.
 *
 * Let op bij vertalen: beweringen niet sterker maken dan het Nederlands.
 */

const NL = {
  seo: {
    title: 'TestWisely - Online Toetsplatform met AI Nakijken',
    description: 'TestWisely: digitaal toetsplatform voor middelbare scholen. AI nakijken, beveiligde iPad app, live monitoring en Kennisnet Entree. Plan een demo.',
    keywords: 'toetsplatform, toetsplatform onderwijs, toetsplatform middelbare school, toetsplatform middelbare scholen, online toetsplatform, digitaal toetsen, digitale toets maken, toetsplatform voortgezet onderwijs, online toetsen maken, toetssoftware onderwijs, AI nakijken, beveiligd toetsen, formatief toetsen, summatief toetsen, Kennisnet Entree, iPad toets app, toetsen VO, toetsplatform docenten, testwisely',
    videoNaam: 'TestWisely Demo - Online Toetsplatform',
    videoBeschrijving: 'Bekijk hoe TestWisely werkt: digitaal toetsen maken, afnemen en nakijken met AI. Het complete toetsplatform voor middelbare scholen.',
  },
  hero: {
    titel: ['Het toetsplatform', 'met AI nakijken'],
    subtitel: 'Het toetsplatform van de toekomst',
    beschrijving: 'Een slim, veilig en AI-gedreven toetsplatform dat docenten tijd teruggeeft en onderwijs naar een nieuw tijdperk brengt.',
    knopDemo: 'PLAN EEN DEMO',
    knopContact: 'NEEM CONTACT OP',
  },
  stats: {
    leerlingen: 'Leerlingen toetsen met TestWisely',
    docenten: 'Docenten besparen tijd met AI',
    nakijktijd: 'Minder nakijktijd',
  },
  features: {
    titel: { nadruk: 'Alles', na: ' op één platform' },
    subtitel: 'Makkelijk. Overzichtelijk. Snel.',
    beschrijving: [
      'Met TestWisely regel je het hele toetsproces vanuit één centraal platform.',
      'Van toetsen maken tot afnemen, nakijken en analyseren, alles op één plek.',
    ],
    ai: {
      naam: 'Nakijken met AI',
      voor: 'Open vragen worden nagekeken op basis van ',
      nadruk: 'jouw antwoordmodel',
      na: '. Bij elk antwoord zie je de onderbouwing, en jij beslist over de punten.',
    },
    opslag: {
      naam: 'Nederlandse data opslag',
      voor: 'De gegevens van leerlingen worden ',
      nadruk: 'opgeslagen in Nederland',
      na: '. Voor het nakijken gaan antwoorden niet naar een externe AI-dienst. Welke partijen meewerken, lees je bij ',
      link: 'subverwerkers',
      slot: '.',
    },
    beveiligd: {
      naam: 'Beveiligd afnemen',
      voor: 'Op de iPad werken leerlingen in de ',
      nadruk: 'vergrendelde TestWisely-app',
      na: ', op Windows in de beveiligde TestWisely-browser. Met live monitoring zie je meteen wie de toets verlaat.',
    },
    zermelo: {
      naam: 'Koppeling met Zermelo',
      voor: 'Met Zermelo komen ',
      nadruk: 'klassen, leerlingen en het rooster',
      na: ' automatisch in TestWisely. Geen Zermelo? Dan lees je de klassen in met een lijst.',
    },
    sso: {
      naam: 'Inloggen met schoolaccount',
      voor: 'Gebruik je bestaande schoolinlog (SSO). ',
      nadruk: 'Geen extra accounts of wachtwoorden nodig',
      na: ', direct veilig toegang.',
    },
    overzicht: {
      naam: 'Alles overzichtelijk op één plek',
      voor: 'Toetsen maken, afnemen, nakijken en analyseren. ',
      nadruk: 'Alles vanuit één centraal dashboard',
      na: '. Volledig overzicht per klas en per leerling.',
    },
    cta: 'VRAAG NU EEN OFFERTE AAN',
  },
  partners: {
    titel: { nadruk: 'Vertrouwd', na: ' door het onderwijs' },
    inGebruik: 'In gebruik bij',
  },
  waarom: {
    titel: ['Waarom kiezen', 'voor TestWisely?'],
    subtitel: [
      'Scholen kiezen voor TestWisely omdat het meer is dan een toetstool.',
      'Het is een complete oplossing die tijd bespaart, inzicht geeft en het onderwijs echt vooruit helpt.',
    ],
    dashboard: {
      titel: 'Dashboard',
      klas: 'Klassenoverzicht 2 HV1',
      gemiddelde: 'KLAS-GEMIDDELDE',
      verschil: '+4% t.o.v. vorige toets',
      voortgang: 'VOORTGANG',
      leerdoel: 'PER LEERDOEL',
      nagekeken: 'NAGEKEKEN',
      voltooid: '78% voltooid',
    },
    punten: [
      { naam: 'Echte tijdsbesparing', tekst: 'Automatische correctie (ook bij open vragen) bespaart docenten uren nakijkwerk.' },
      { naam: 'Slimme AI-docent', tekst: 'Onze speciaal ontwikkelde AI kijkt niet alleen na, maar helpt docenten toetsen en onderwijs te verbeteren.' },
      { naam: 'Inzicht in wat leerlingen nodig hebben', tekst: 'Dankzij duidelijke analytics weten docenten precies waar leerlingen vastlopen, geen giswerk meer.' },
      { naam: 'Een account, een keer inloggen', tekst: 'Gebruikers kunnen inloggen met hun standaard schoolaccount. Dezelfde inloggegevens die op school worden gebruikt, werken direct in TestWisely. Geen extra accounts, geen nieuwe wachtwoorden, gewoon meteen aan de slag.' },
    ],
    luisteren: {
      naam: 'We luisteren echt naar docenten',
      voor: 'Feedback van docenten staat centraal bij TestWisely. Ideeën, wensen of problemen mail je naar ',
      na: '. En ja, ',
      vet: 'we doen daar ook echt iets mee.',
      slot: ' Omdat wij samen met docenten het beste uit het onderwijs willen halen.',
    },
    extra: [
      { naam: 'Veilig en betrouwbaar toetsen', tekst: 'Met de vergrendelde iPad-app, de beveiligde Windows-browser en live monitoring blijft een toets eerlijk en controleerbaar.' },
      { naam: 'Gebruiksvriendelijk voor iedereen', tekst: 'Geen ingewikkelde systemen. Docenten en leerlingen kunnen direct aan de slag.' },
      { naam: 'Klaar voor modern onderwijs', tekst: 'Volledig digitaal en duurzaam, met voorlezen en extra tijd voor leerlingen die dat nodig hebben.' },
    ],
    grafiek: 'Resultaten',
  },
  vakken: {
    label: 'GESCHIKT VOOR',
    titel: { nadruk: 'Elk vak', na: ' in het onderwijs' },
    namen: {
      wiskunde: 'Wiskunde',
      scheikunde: 'Scheikunde',
      natuurkunde: 'Natuurkunde',
      biologie: 'Biologie',
      engels: 'Engels',
      nederlands: 'Nederlands',
      duits: 'Duits',
      frans: 'Frans',
      geschiedenis: 'Geschiedenis',
      aardrijkskunde: 'Aardrijkskunde',
      economie: 'Economie',
      informatica: 'Informatica',
    },
    meer: '+17 meer',
    perVak: 'Digitaal toetsen per vak',
    paginas: {
      wiskunde: 'Wiskunde',
      talen: 'Talen',
      'geschiedenis-en-aardrijkskunde': 'Geschiedenis en aardrijkskunde',
    },
  },
  kennisbank: {
    titel: { voor: 'Uit de ', nadruk: 'kennisbank' },
    beschrijving: 'Praktische artikelen over digitaal toetsen, nakijken en privacy.',
    alle: 'Alle artikelen',
  },
  reviews: [
    {
      quote: 'TestWisely heeft ons digitaal toetsen naar een hoger niveau getild. De AI-nakijkhulp bespaart me gemiddeld 3 uur per week. En de leerlingen vinden het fijn dat ze direct feedback krijgen op hun antwoorden.',
      name: 'Docent',
      role: 'Lyceum het Nieuwe Leren',
    },
    {
      quote: 'Eindelijk een platform dat begrijpt wat docenten nodig hebben. Het nakijken van open vragen gaat nu automatisch en de feedback aan leerlingen is persoonlijk. Dat scheelt mij uren per week.',
      name: 'Lisa de V.',
      role: 'Docent Nederlands',
    },
    {
      quote: 'Mijn leerlingen vinden het geweldig. De interface is modern en overzichtelijk. En doordat alles via hun schoolaccount werkt, zijn er geen technische problemen meer bij het inloggen.',
      name: 'Mark B.',
      role: 'Docent Wiskunde',
    },
    {
      quote: 'De live monitoring is een gamechanger. Ik kan precies zien wie er bezig is, wie vastloopt en of er verdacht gedrag is. Fraude is echt geen optie meer met TestWisely.',
      name: 'Sandra T.',
      role: 'Docent Scheikunde',
    },
    {
      quote: 'Als teamleider waardeer ik vooral de statistieken per leerdoel. We kunnen nu data-gedreven beslissingen nemen over ons curriculum. En het scheelt enorm veel papier en printkosten.',
      name: 'Peter H.',
      role: 'Teamleider bovenbouw',
    },
  ],
  reviewLabel: 'Review',
};

const EN = {
  seo: {
    title: 'TestWisely - Online Assessment Platform with AI Marking',
    description: 'TestWisely: digital assessment platform for secondary schools in the Netherlands. AI marking, secure iPad app, live monitoring and Kennisnet Entree. Schedule a demo.',
    keywords: 'assessment platform, online assessment platform, digital assessment, digital tests secondary school, create online tests, assessment software education, AI marking, secure testing, formative assessment, summative assessment, Kennisnet Entree, iPad test app, testwisely',
    videoNaam: 'TestWisely Demo - Online Assessment Platform',
    videoBeschrijving: 'See how TestWisely works: creating, administering and marking digital tests with AI. The complete assessment platform for secondary schools.',
  },
  hero: {
    titel: ['The assessment platform', 'with AI marking'],
    subtitel: 'The assessment platform of the future',
    beschrijving: 'A smart, secure and AI-driven assessment platform that gives teachers back their time and takes education into a new era.',
    knopDemo: 'SCHEDULE A DEMO',
    knopContact: 'CONTACT US',
  },
  stats: {
    leerlingen: 'Students take tests with TestWisely',
    docenten: 'Teachers save time with AI',
    nakijktijd: 'Less marking time',
  },
  features: {
    titel: { nadruk: 'Everything', na: ' on one platform' },
    subtitel: 'Easy. Clear. Fast.',
    beschrijving: [
      'With TestWisely you manage the entire testing process from one central platform.',
      'From creating tests to administering, marking and analysing them, all in one place.',
    ],
    ai: {
      naam: 'Marking with AI',
      voor: 'Open questions are marked based on ',
      nadruk: 'your answer model',
      na: '. For every answer you see the reasoning, and you decide on the points.',
    },
    opslag: {
      naam: 'Data stored in the Netherlands',
      voor: 'Student data is ',
      nadruk: 'stored in the Netherlands',
      na: '. For marking, answers are not sent to an external AI service. Which parties are involved is listed under ',
      link: 'subprocessors',
      slot: '.',
    },
    beveiligd: {
      naam: 'Secure test-taking',
      voor: 'On the iPad, students work in the ',
      nadruk: 'locked TestWisely app',
      na: '; on Windows, in the secure TestWisely browser. With live monitoring you see straight away who leaves the test.',
    },
    zermelo: {
      naam: 'Integration with Zermelo',
      voor: 'With Zermelo, ',
      nadruk: 'classes, students and the timetable',
      na: ' come into TestWisely automatically. No Zermelo? Then you import the classes from a list.',
    },
    sso: {
      naam: 'Log in with a school account',
      voor: 'Use your existing school login (SSO). ',
      nadruk: 'No extra accounts or passwords needed',
      na: ', secure access straight away.',
    },
    overzicht: {
      naam: 'Everything clearly in one place',
      voor: 'Creating, administering, marking and analysing tests. ',
      nadruk: 'All from one central dashboard',
      na: '. A complete overview per class and per student.',
    },
    cta: 'REQUEST A QUOTE NOW',
  },
  partners: {
    titel: { nadruk: 'Trusted', na: ' by education' },
    inGebruik: 'In use at',
  },
  waarom: {
    titel: ['Why choose', 'TestWisely?'],
    subtitel: [
      'Schools choose TestWisely because it is more than a testing tool.',
      'It is a complete solution that saves time, provides insight and genuinely moves education forward.',
    ],
    dashboard: {
      titel: 'Dashboard',
      klas: 'Class overview 2 HV1',
      gemiddelde: 'CLASS AVERAGE',
      verschil: '+4% vs. previous test',
      voortgang: 'PROGRESS',
      leerdoel: 'PER LEARNING OBJECTIVE',
      nagekeken: 'MARKED',
      voltooid: '78% complete',
    },
    punten: [
      { naam: 'Real time savings', tekst: 'Automatic marking (open questions included) saves teachers hours of marking.' },
      { naam: 'Smart AI teacher', tekst: 'Our purpose-built AI does not just mark, it also helps teachers improve their tests and teaching.' },
      { naam: 'Insight into what students need', tekst: 'Clear analytics show teachers exactly where students get stuck, no more guesswork.' },
      { naam: 'One account, one login', tekst: 'Users can log in with their standard school account. The same login details used at school work straight away in TestWisely. No extra accounts, no new passwords, just get started.' },
    ],
    luisteren: {
      naam: 'We really listen to teachers',
      voor: 'Teacher feedback is central to TestWisely. Email your ideas, wishes or problems to ',
      na: '. And yes, ',
      vet: 'we really do act on it.',
      slot: ' Because we want to get the best out of education together with teachers.',
    },
    extra: [
      { naam: 'Safe and reliable testing', tekst: 'With the locked iPad app, the secure Windows browser and live monitoring, a test stays fair and verifiable.' },
      { naam: 'User-friendly for everyone', tekst: 'No complicated systems. Teachers and students can get started straight away.' },
      { naam: 'Ready for modern education', tekst: 'Fully digital and sustainable, with read aloud and extra time for students who need it.' },
    ],
    grafiek: 'Results',
  },
  vakken: {
    label: 'SUITABLE FOR',
    titel: { nadruk: 'Every subject', na: ' in education' },
    namen: {
      wiskunde: 'Mathematics',
      scheikunde: 'Chemistry',
      natuurkunde: 'Physics',
      biologie: 'Biology',
      engels: 'English',
      nederlands: 'Dutch',
      duits: 'German',
      frans: 'French',
      geschiedenis: 'History',
      aardrijkskunde: 'Geography',
      economie: 'Economics',
      informatica: 'Computer science',
    },
    meer: '+17 more',
    perVak: 'Digital assessment by subject',
    paginas: {
      wiskunde: 'Mathematics',
      talen: 'Languages',
      'geschiedenis-en-aardrijkskunde': 'History and geography',
    },
  },
  kennisbank: {
    titel: { voor: 'From the ', nadruk: 'knowledge base' },
    beschrijving: 'Practical articles (in Dutch) about digital assessment, marking and privacy.',
    alle: 'All articles',
  },
  reviews: [
    {
      quote: 'TestWisely has taken our digital assessment to a higher level. The AI marking assistant saves me 3 hours a week on average. And students like getting feedback on their answers straight away.',
      name: 'Teacher',
      role: 'Lyceum het Nieuwe Leren',
    },
    {
      quote: 'Finally a platform that understands what teachers need. Marking open questions now happens automatically and the feedback to students is personal. That saves me hours every week.',
      name: 'Lisa de V.',
      role: 'Teacher of Dutch',
    },
    {
      quote: 'My students love it. The interface is modern and clear. And because everything works through their school account, there are no more technical problems when logging in.',
      name: 'Mark B.',
      role: 'Teacher of Mathematics',
    },
    {
      quote: 'Live monitoring is a game changer. I can see exactly who is working, who is stuck and whether there is suspicious behaviour. Cheating is really no longer an option with TestWisely.',
      name: 'Sandra T.',
      role: 'Teacher of Chemistry',
    },
    {
      quote: 'As a team leader, I especially value the statistics per learning objective. We can now make data-driven decisions about our curriculum. And it saves an enormous amount of paper and printing costs.',
      name: 'Peter H.',
      role: 'Team leader, upper secondary',
    },
  ],
  reviewLabel: 'Review',
};

const HOME_TEKSTEN = { nl: NL, en: EN };
export default HOME_TEKSTEN;
